import React, { useEffect, useState, useMemo } from "react";
import Breadcrumb from "../../components/common/Breadcrumb";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import {
  DefaultButton,
  DefaultPrimaryButton,
  InputWithLabel,
  SelectBox,
} from "../../components/micro";
import { getRecipeList } from "../../redux/nutrition-recipes/slice";
import { getTipsList } from "../../redux/nutrition-tips/slice";
import { getAudioList } from "../../redux/audio/slice";
import { createSection, updateSection } from "../../redux/section/slice";
import ConfirmationModel from "../../components/common/ConfirmationModel";

const AddEditSection = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isEditMode = location?.state?.mode;
  const sectionData = location?.state?.section || {};
  const [selectionType, setSelectionType] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [formValues, setFormValues] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const storeData = useSelector((store) => ({
    recipes: store?.recipeData?.getRecipeListStatus?.data?.data,
    tips: store?.tipsData?.getTipsListStatus?.data?.data,
    audios: store?.audioData?.getAudioListStatus?.data?.data,
  }));
  const recipes = storeData.recipes || [];
  const tips = storeData.tips || [];
  const audios = storeData.audios || [];

  useEffect(() => {
    if (!selectionType) return;

    const params = {
      search: "",
      pageCount: 1,
      pageLimit: 100,
      sort_by: "createdAt",
      sort_direction: "desc",
      limit: 100,
    };

    if (selectionType === "recipe") dispatch(getRecipeList(params));
    if (selectionType === "tips") dispatch(getTipsList(params));
    if (selectionType === "audio") dispatch(getAudioList(params));
  }, [selectionType, dispatch]);

  const recipeOptions = useMemo(
    () =>
      (recipes?.data || [])?.map((item) => ({
        label: item.recipeName,
        value: item._id,
      })),
    [recipes],
  );

  const tipsOptions = useMemo(
    () =>
      (tips?.data || [])?.map((item) => ({
        label: item.tipName,
        value: item._id,
      })),
    [tips],
  );

  const audioOptions = useMemo(
    () =>
      (audios?.data || [])?.map((item) => ({
        label: item.audioName,
        value: item._id,
      })),
    [audios],
  );

  const selectionTypeOptions = [
    { label: "Recipe", value: "recipe" },
    { label: "Tips", value: "tips" },
    { label: "Audio", value: "audio" },
  ];

  const validationSchema = Yup.object().shape({
    sectionName: Yup.string().required("Section name required"),
    selectionType: Yup.string().required("Selection type required"),
    items: Yup.array()
      .min(1, "Please select at least one item")
      .required("Please select at least one item"),
  });

  const handleItemToggle = (id, setFieldValue, values) => {
    let updated;

    if (values.items.includes(id)) {
      updated = values.items.filter((item) => item !== id);
    } else {
      updated = [...values.items, id];
    }

    setSelectedItems(updated); // local state
    setFieldValue("items", updated); // Formik state
  };

  /* PREFILL DATA IN EDIT MODE */
  useEffect(() => {
    if (!isEditMode || !sectionData) return;

    // set selection type
    setSelectionType(sectionData.selectionType);

    // set selected items (IDs)
    if (sectionData?.data?.length) {
      const ids = sectionData.data.map((item) => item._id);
      setSelectedItems(ids);
    }
  }, [isEditMode, sectionData]);

  const handleConfirm = () => {
    setIsLoading(true);

    const payload = {
      sectionName: formValues.sectionName,
      selectionType,
      sectionArray: selectedItems,
    };

    const action = isEditMode
      ? updateSection({
          id: sectionData._id,
          data: payload,
        })
      : createSection(payload);

    dispatch(action)
      .then((res) => {
        if (
          res.type === "updateSection/fulfilled" ||
          res.type === "createSection/fulfilled"
        ) {
          navigate("/section");
        }
      })
      .finally(() => {
        setIsLoading(false);
        setShowConfirmationModal(false);
      });
  };
  const handleEditClick = () => setIsEditing(true);
  const handleCancelClick = () => navigate("/section");

  return (
    <>
      {/* HEADER */}
      <div className="mb-5">
        <h6 className="font-Montserrat font-medium text-xl mb-3">
          {isEditMode ? "Edit Section" : "Create Section"}
        </h6>
        <Breadcrumb
          breadCrumbParent={"Home"}
          breadCrumbActive={isEditMode ? "Edit Section" : "Create Section"}
        />
      </div>

      {/* FORM */}
      <div className="bg-white shadow-md rounded-lg p-6">
        <Formik
          initialValues={{
            sectionName: sectionData?.sectionName || "",
            selectionType: sectionData?.selectionType || "",
            items: sectionData?.data?.map((item) => item._id) || [],
          }}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            setFormValues(values);
            setShowConfirmationModal(true);
          }}
        >
          {({
            values,
            handleChange,
            handleBlur,
            errors,
            touched,
            setFieldValue,
            setFieldTouched, 
          }) => (
            <Form>
              <div className="grid grid-cols-2 gap-6">
                {/* Section Name */}
                <InputWithLabel
                  name="sectionName"
                  label="Section Name*"
                  value={values.sectionName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errors={errors.sectionName && touched.sectionName}
                  errorsText={errors.sectionName}
                  className={`${
                    errors?.sectionName && touched?.sectionName
                      ? `border border-red`
                      : `border border-borderTheme/80 focus-within:border-themeBtn `
                  } w-full `}
                  disabled={isEditMode === "edit" && !isEditing}
                />

                {/* Selection Type */}
                <SelectBox
                  name="selectionType"
                  label="Selection Type"
                  placeholder="Select Type*"
                  options={selectionTypeOptions}
                  value={
                    selectionTypeOptions.find(
                      (opt) => opt.value === values.selectionType,
                    ) || null
                  }
                  onChange={(option) => {
                    const value = option?.value ?? "";

                    setFieldValue("selectionType", value);
                    setSelectionType(value);
                    setSelectedItems([]);
                  }}
                  errors={errors.selectionType && touched.selectionType}
                  errorsText={errors.selectionType}
                  isDisabled={isEditMode === "edit" && !isEditing}
                />

                {/* Dynamic Items Dropdown */}

                <SelectBox
                  key={selectionType}
                  name="items"
                  label="Select Items"
                  placeholder="Select Items"
                  options={
                    selectionType === "recipe"
                      ? recipeOptions
                      : selectionType === "tips"
                        ? tipsOptions
                        : audioOptions
                  }
                  value={(selectionType === "recipe"
                    ? recipeOptions
                    : selectionType === "tips"
                      ? tipsOptions
                      : audioOptions
                  ).filter((opt) => selectedItems.includes(opt.value))}
                  onChange={(selected) => {
                    const values = selected
                      ? selected.map((opt) => opt.value)
                      : [];
                    setSelectedItems(values);
                    setFieldValue("items", values);
                    setFieldTouched && setFieldTouched("items", true, false);
                  }}
                  isMulti
                  isDisabled={isEditMode === "edit" && !isEditing}
                  errors={Boolean(errors.items && touched.items)}
                  errorsText={touched.items ? errors.items : ""}
                />
              </div>

              {/* BUTTONS */}
              <div className="flex justify-end gap-3 mt-3">
                <DefaultPrimaryButton
                  type="button"
                  value={isEditMode === "edit" ? "Back" : "Cancel"}
                  onClick={handleCancelClick}
                />
                {isEditMode === "edit" ? (
                  isEditing ? (
                    <>
                      <DefaultButton
                        type="submit"
                        value="Save"
                        onClick={(e) => {}}
                      />
                    </>
                  ) : (
                    <DefaultButton
                      value="Edit"
                      onClick={handleEditClick}
                      type="submit"
                    />
                  )
                ) : (
                  <>
                    <DefaultButton type="submit" value="confirm" />
                  </>
                )}
              </div>
            </Form>
          )}
        </Formik>
      </div>
      {showConfirmationModal && (
        <ConfirmationModel
          confirmText={isEditMode === "edit" ? "Edit" : "Create"}
          onClose={() => setShowConfirmationModal(false)}
          onConfirm={handleConfirm}
          isLoading={isLoading}
        />
      )}
    </>
  );
};

export default AddEditSection;
